import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { Show, UserButton, useUser } from "@clerk/react";
import { MessageCircle, Inbox, CreditCard, Package, FileText, Briefcase, Settings, ExternalLink, Lock, Star, Bell } from "lucide-react";
import { useAdminApi } from "@/lib/site";
import { Logo } from "@/components/site/Navbar";
import AdminChats from "./AdminChats";
import AdminMessages from "./AdminMessages";
import AdminPayments from "./AdminPayments";
import AdminPackages from "./AdminPackages";
import AdminContent from "./AdminContent";
import AdminCases from "./AdminCases";
import AdminReviews from "./AdminReviews";
import AdminSettings from "./AdminSettings";
import AdminNotifications from "./AdminNotifications";
import AdminProjects from "./AdminProjects";

const NAV = [
  ["Projects", "projects", Briefcase],
  ["Chats", "", MessageCircle], ["Messages", "messages", Inbox], ["Payments", "payments", CreditCard],
  ["Push", "notifications", Bell], ["Packages", "packages", Package], ["Content", "content", FileText], ["Work", "work", Briefcase], ["Reviews", "reviews", Star], ["Settings", "settings", Settings],
];

const Shell = () => (
  <div className="min-h-screen bg-slate-50 lg:flex" data-testid="admin-shell">
    <aside className="hidden lg:flex flex-col w-60 shrink-0 h-screen sticky top-0 border-r bg-white p-4">
      <div className="px-2 py-3"><Logo /></div>
      <nav className="mt-6 space-y-1 flex-1">
        {NAV.map(([l, to, I]) => (
          <NavLink key={l} end to={`/admin/${to}`} data-testid={`admin-nav-${l.toLowerCase()}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            <I className="w-4 h-4" /> {l}
          </NavLink>
        ))}
      </nav>
      <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-900" data-testid="admin-view-site"><ExternalLink className="w-4 h-4" /> View site</a>
      <div className="px-3 py-3 border-t mt-2 flex items-center gap-3"><UserButton /><span className="text-xs text-slate-500">Owner</span></div>
    </aside>
    <div className="flex-1 min-w-0 pb-20 lg:pb-0">
      <header className="lg:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b px-4 h-14 flex items-center justify-between"><Logo /><UserButton /></header>
      <main className="p-4 sm:p-6 lg:p-8 max-w-6xl">
        <Routes>
          <Route path="projects" element={<AdminProjects />} />
          <Route index element={<AdminChats />} />
          <Route path="messages" element={<AdminMessages />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="packages" element={<AdminPackages />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="work" element={<AdminCases />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="notifications" element={<AdminNotifications />} />
        </Routes>
      </main>
    </div>
    <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t flex overflow-x-auto" data-testid="admin-mobile-nav">
      {NAV.map(([l, to, I]) => (
        <NavLink key={l} end to={`/admin/${to}`} data-testid={`admin-mobile-nav-${l.toLowerCase()}`}
          className={({ isActive }) => `flex flex-col shrink-0 min-w-[64px] flex-1 items-center gap-0.5 py-2.5 text-[9px] font-medium truncate ${isActive ? "text-blue-600" : "text-slate-500"}`}>
          <I className="w-5 h-5" />{l}
        </NavLink>
      ))}
    </nav>
  </div>
);

const Gate = () => {
  const call = useAdminApi();
  const { user } = useUser();
  const [role, setRole] = useState(null);
  useEffect(() => { call("get", "/me").then((d) => setRole(d.role)).catch(() => setRole("error")); }, [call]);
  if (!role) return <div className="min-h-screen grid place-items-center text-slate-500" data-testid="admin-loading">Loading…</div>;
  if (role === "admin") return <Shell />;
  return (
    <div className="min-h-screen grid place-items-center px-5 grid-lines" data-testid="admin-no-access">
      <div className="max-w-md text-center rounded-3xl bg-white border p-10">
        <Lock className="w-10 h-10 text-slate-400 mx-auto" />
        <h1 className="mt-5 font-display text-2xl font-bold">No admin access</h1>
        <p className="mt-2 text-slate-600 text-sm">You're signed in as {user?.primaryEmailAddress?.emailAddress}. This account isn't set up as the site owner.</p>
        <div className="mt-6 flex justify-center"><UserButton /></div>
      </div>
    </div>
  );
};

export default function AdminApp() {
  return (
    <>
      <Show when="signed-in"><Gate /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
